import logger from '../../utils/logger-rum';
import React, {
	useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import type { Invitation } from '../../stores/types/invitations';
import { ConnectedUser } from '../../stores/types/connection';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useShots from '../../contexts/shots/provider';
import EmptyScreenShare from './empty-screen-share';
import { ROOM_STATES } from '../../stores/reducers/settings';
import useMissingScreenShareAlert from '../../hooks/use-missing-screen-share-alert';

interface Props {
	mediaId: string | number;
	feedPosition: number;
}

export default function FeedScreenShare(props: Props) {
	const { feedPosition, mediaId: email } = props;
	const isEmbed = useSelector((s: GS) => s.SessionReducer.embed);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const videoElRef = useRef<HTMLVideoElement>(null);

	const { idsOfVideosInCanvas } = useShots();
	const { screenShareVideo } = useMissingScreenShareAlert(email as string);

	const isOnCanvas = useMemo(
		() => screenShareVideo && idsOfVideosInCanvas.includes(screenShareVideo.id),
		[screenShareVideo, idsOfVideosInCanvas],
	);

	const userFromEmail = useMemo(() => {
		const connectedUser = connectedUsers.find((user: ConnectedUser) => user.email === email);
		if (connectedUser) return connectedUser.gr_user;
		const invite = invitations.find((invitation: Invitation) => invitation.profile.email === email);
		if (invite?.profile.gr_user) return invite.profile.gr_user;
		return 0;
	}, [email, connectedUsers, invitations]);

	useEffect(() => {
		if (videoElRef.current && screenShareVideo
			&& screenShareVideo?.srcObject !== videoElRef.current.srcObject) {
			videoElRef.current.srcObject = screenShareVideo.srcObject;
			videoElRef.current.volume = 0;
			if (!screenShareVideo.srcObject.active) return;
			videoElRef.current.play().then(() => {
				if (videoElRef.current?.paused) {
					videoElRef.current.play().catch((e) => {
						logger.error('video el play request error', {
							location: 'feed-screenshare second',
						}, e);
					});
				}
			}).catch((e) => {
				logger.error('video el play request error', {
					location: 'feed-screenshare first',
				}, e);
			});
			cleanupVideoStreamElement(videoElRef.current);
		}
	}, [screenShareVideo?.srcObject]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || isEmbed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		screenShareVideo && screenShareVideo?.srcObject.active
			? <>
					<video
						key={screenShareVideo.srcObject.id + feedPosition}
						muted={true}
						autoPlay={true}
						poster={blackImage}
						playsInline
						ref={videoElRef}
					>
					</video>
			</>
			: <>
					<EmptyScreenShare grUserId={userFromEmail} />
				</>
	);
}
